import axios, { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import SelectionCard, { TextInput } from '../../components/Inputs';
import BodyWrap from '../../components/BodyWrap';
import { Accordion, Select, Textarea } from '@mantine/core';
import { GradientButton } from '../../components/Buttons';
import { IconBinaryTree2Filled } from '@tabler/icons-react';
import { clearLocalStorage } from '../../utils/authUtils';
import { validateKey } from '../../utils/commonUtils';
import { ErrorMessage } from '../../components/Messages';

function DatablockBuilder() {
  useEffect(() => {
    document.title = 'Build datablock | PromptSlate';
  }, []);
  const { project_key } = useParams();
  const navigate = useNavigate();
  let [selectedDatablockType, setSelectedDatablockType] = useState<string>('STANDARD_PROMPT');
  let [datablockName, setDatablockName] = useState<string>('');
  let [datablockKey, setDatablockKey] = useState<string>('');
  let [modelName, setModelName] = useState<string>('');
  let [locale, setLocale] = useState<string>('');
  let [standardPromptRole, setStandardPromptRole] = useState<string>('');
  let [standardPrompt, setStandardPrompt] = useState<string>('');
  let [variableData, setVariableData] = useState<string>('');
  let [message, setMessage] = useState<string>('');

  const handleSelection = (selection: string) => {
    setSelectedDatablockType(selection);
  };
  const isSelected = (check: string) => {
    if (check === selectedDatablockType) {
      return true;
    }
    return false;
  };

  const handleCreateDatablockSubmit = async () => {
    setMessage('');
    try {
      // Validate datablock key
      const validKey = validateKey(datablockKey);
      if (!validKey) {
        setMessage(
          'Datablock key should only contain lowercase alphabets and underscores (e.g., bot_persona)'
        );
        return;
      }
      const authToken = localStorage.getItem('authToken');
      const headers = {
        Authorization: 'Bearer ' + authToken,
      };

      let datablockData = {};
      switch (selectedDatablockType) {
        case 'STANDARD_PROMPT':
          datablockData = {
            role: standardPromptRole,
            prompt: standardPrompt,
          };
          break;
        case 'VARIABLE_DATA':
          datablockData = {
            variable: variableData,
          };
      }

      const datablockMetadata = {
        ...(modelName && { model: modelName }),
        ...(locale && { locale }),
      };

      const payload: Record<string, any> = {
        project_key,
        type: selectedDatablockType,
        name: datablockName,
        key: datablockKey,
        data: datablockData,
      };

      if (Object.keys(datablockMetadata).length > 0) {
        payload.metadata = datablockMetadata;
      }

      await axios.post(`${process.env.REACT_APP_API_URL}/datablock/create`, payload, { headers });
      navigate(`/project/${project_key}`);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if (axiosError.status === 401) {
          clearLocalStorage();
          navigate('/login');
        }
        if (axiosError.code === 'ERR_BAD_REQUEST') {
          setMessage('Invalid request');
        } else {
          setMessage('A network error occured. Please try again later.');
        }
      } else {
        setMessage('An error occured. Please try again later');
      }
    }
  };

  const renderBuilder = () => {
    switch (selectedDatablockType) {
      case 'STANDARD_PROMPT':
        return (
          <div>
            <div className="mt-8">
              <div className="text-lg">Prompt role</div>
              <Select
                placeholder="Select prompt role"
                data={['system', 'user']}
                allowDeselect={false}
                className="w-[350px] mt-5"
                onChange={(value) => {
                  if (value !== null) {
                    setStandardPromptRole(value);
                  }
                }}
              />
            </div>
            <div className="mt-8">
              <div className="text-lg">Prompt</div>
              <Textarea
                placeholder="Enter your prompt"
                className="mt-5 w-full"
                minRows={8}
                autosize
                resize="vertical"
                onChange={(e) => setStandardPrompt(e.target.value)}
              />
            </div>
          </div>
        );
      case 'VARIABLE_DATA':
        return (
          <div>
            <div className="text-lg mt-8">Data</div>
            <Textarea
              placeholder="Enter data"
              className="mt-5 w-full"
              minRows={8}
              autosize
              resize="vertical"
              onChange={(e) => setVariableData(e.target.value)}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <BodyWrap>
      <div className="pb-5">
        <div className="text-xl">
          <NavLink to={`/dashboard`}>Projects</NavLink> /{' '}
          <NavLink to={`/project/${project_key}`}>{project_key} </NavLink>/ Build Datablock
        </div>
        <div className="mt-6 max-w-screen-xl">
          <div>
            <div className="text-lg">Select datablock type</div>
            <div className="grid grid-cols-3 gap-4 mt-5">
              <SelectionCard
                title="Standard Prompt"
                subTitile="Standard prompt format with role and prompt text"
                isSelected={isSelected('STANDARD_PROMPT')}
                onClick={() => {
                  handleSelection('STANDARD_PROMPT');
                }}
              />
              <SelectionCard
                title="Variable text"
                subTitile="A simple variable in which you can store text"
                isSelected={isSelected('VARIABLE_DATA')}
                onClick={() => {
                  handleSelection('VARIABLE_DATA');
                }}
              />
            </div>
          </div>
          <div className="mt-8">
            <div className="text-lg">Datablock details</div>
            <div className="flex flex-row gap-8">
              <TextInput
                placeholder="Datablock name"
                className="w-[350px] mt-5"
                value={datablockName}
                onChange={(e) => setDatablockName(e.target.value)}
              />{' '}
              <TextInput
                placeholder="Datablock unique key"
                className="w-[350px] mt-5"
                value={datablockKey}
                onChange={(e) => setDatablockKey(e.target.value)}
              />
            </div>
          </div>
          <div className="mt-8">
            <Accordion variant="contained" className="bg-white">
              <Accordion.Item value="metadata">
                <Accordion.Control
                  icon={<IconBinaryTree2Filled size={20} className="text-slate-600" />}
                >
                  Metadata (optional)
                </Accordion.Control>
                <Accordion.Panel className="bg-white">
                  <div className="flex flex-row gap-8">
                    <TextInput
                      placeholder="Model name (eg. gpt-4o, claude-3-sonnet)"
                      className="w-[350px] mt-5"
                      value={modelName}
                      onChange={(e) => setModelName(e.target.value)}
                    />{' '}
                    <TextInput
                      placeholder="Locale (eg. en-GB)"
                      className="w-[350px] mt-5"
                      value={locale}
                      onChange={(e) => setLocale(e.target.value)}
                    />
                  </div>
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
            <div className="text-lg"></div>
          </div>
          {renderBuilder()}
          <ErrorMessage className="mt-5">{message}</ErrorMessage>
          <GradientButton className="mt-8 float-right" onClick={handleCreateDatablockSubmit}>
            Save Datablock
          </GradientButton>
        </div>
      </div>
    </BodyWrap>
  );
}

export default DatablockBuilder;
